import {
  useEstimatedDelivery,
  useCurrentComposition,
  useCachedDeliveryDate
} from "@design-lab/entities/composition";
import { usePdpDeliveryData } from "@design-lab/entities/composition/model";
import type { PdpDeliveryData } from "@design-lab/entities/composition/model/usePdpDeliveryData";
import { useCachedEstimatedDeliveryDate } from "@design-lab/shared/lib/config";
import {
  extractDayFromDate,
  formatDeliveryTimeRange,
  formatTimeAMPM,
  shouldShowTimeRange
} from "common/utils/format/date";
import type { ID } from "common/utils/models";

const DEFAULT_COMPOSITION_ID = "9460" as ID;
const CEIL_HOUR_DEFAULT = false;

type CachedDeliveryDateParams = {
  fetchedCachedDeliveryDate: string;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

type EstimatedDeliveryDateParams = {
  pdpDeliveryData: PdpDeliveryData | undefined;
  estimatedDeliveryData:
    | {
        valueFull?: string;
        deliveryTime?: string;
        deliveryTimeEnd?: string;
        timeleft?: string | Date;
      }
    | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

function getCachedDeliveryDateResult({
  fetchedCachedDeliveryDate,
  isLoading,
  isFetching,
  isError
}: CachedDeliveryDateParams) {
  // Use only fetchedCachedDeliveryDate - don't fall back to initialCachedDeliveryDate
  // This function is only called when we have a valid cached date
  return {
    valueFull: fetchedCachedDeliveryDate,
    timeRange: undefined,
    formattedTimeLeft: undefined,
    isLoading: isLoading || isFetching,
    isError
  };
}

function getEstimatedDeliveryDateResult({
  pdpDeliveryData,
  estimatedDeliveryData,
  isLoading,
  isFetching,
  isError
}: EstimatedDeliveryDateParams) {
  const valueFull = pdpDeliveryData?.valueFull || estimatedDeliveryData?.valueFull;
  const deliveryTime = pdpDeliveryData?.deliveryTime || estimatedDeliveryData?.deliveryTime;
  const deliveryTimeEnd =
    pdpDeliveryData?.deliveryTimeEnd || estimatedDeliveryData?.deliveryTimeEnd;
  const timeLeft = pdpDeliveryData?.timeleft || estimatedDeliveryData?.timeleft;

  const isTimeRangeAvailable =
    valueFull && shouldShowTimeRange(valueFull) && deliveryTime && deliveryTimeEnd;

  const timeRange = isTimeRangeAvailable
    ? `${extractDayFromDate(valueFull)} ${formatDeliveryTimeRange(deliveryTime, deliveryTimeEnd)}`
    : undefined;

  const formattedTimeLeft = timeLeft
    ? formatTimeAMPM(timeLeft.toString(), CEIL_HOUR_DEFAULT)
    : undefined;

  return {
    valueFull,
    timeRange,
    formattedTimeLeft,
    isLoading: isLoading || isFetching,
    isError
  };
}

export function useFormattedDeliveryDate() {
  const initialCachedDeliveryDate = useCachedEstimatedDeliveryDate();

  const { currentComposition } = useCurrentComposition();
  const compositionId = currentComposition?.id ?? DEFAULT_COMPOSITION_ID;

  // Cached delivery date hook - disabled on mount, only refetches when address is updated
  const {
    data: fetchedCachedDeliveryDate,
    isLoading: isCachedLoading,
    isError: isCachedError,
    isFetching: isCachedFetching,
    isFetched: hasCachedBeenFetched
  } = useCachedDeliveryDate();

  // Always try cached delivery date first
  // Use fetchedCachedDeliveryDate if available (from cache updates via fetchQuery)
  // Otherwise use initialCachedDeliveryDate if query hasn't been fetched yet
  // If fetched and returned null or errored, don't use stale initial value - fall back to estimated delivery
  let cachedDate: string | undefined;
  if (fetchedCachedDeliveryDate != null) {
    cachedDate = fetchedCachedDeliveryDate;
  } else if (hasCachedBeenFetched || isCachedError) {
    // Query was fetched but returned null, or there was an error (e.g., 503)
    // Don't use stale initial - fall back to estimated delivery
    cachedDate = undefined;
  } else {
    // Query hasn't been fetched yet, use initial value
    cachedDate = initialCachedDeliveryDate;
  }

  // Estimated delivery hook - only fetch if cached delivery is not available
  // Passing undefined disables the query (useEstimatedDelivery has enabled: !!compositionId)
  const compositionIdForEstimate = !cachedDate ? compositionId : undefined;
  const {
    data: estimatedDeliveryData,
    isLoading: isEstimatedLoading,
    isError: isEstimatedError,
    isFetching: isEstimatedFetching
  } = useEstimatedDelivery(compositionIdForEstimate);

  const { data: pdpDeliveryData } = usePdpDeliveryData();

  // If cached delivery date exists, return it
  // Never show error when we have valid cached data
  if (cachedDate) {
    return getCachedDeliveryDateResult({
      fetchedCachedDeliveryDate: cachedDate,
      isLoading: isCachedLoading,
      isFetching: isCachedFetching,
      isError: false // Never show error when we have valid cached data
    });
  }

  // Fall back to estimated delivery date if cached is undefined
  return getEstimatedDeliveryDateResult({
    pdpDeliveryData,
    estimatedDeliveryData,
    isLoading: isEstimatedLoading,
    isFetching: isEstimatedFetching,
    isError: isEstimatedError
  });
}
